'use client';

import { useParams, usePathname } from 'next/navigation';
import { useMemo, type ReactNode } from 'react';

import type { Id } from '@/convex/_generated/dataModel';
import { api, useQuery, useQueryWithStatus } from '@/lib/convex';
import { useBrowserNotificationPermission } from './browser-notification-permission';
import { SidebarInboxDataProvider } from './sidebar-inbox-data';
import { useDesktopNativeNotifications } from './use-desktop-native-notifications';
import { useMinuteClock } from './use-minute-clock';
import { useWebPushOwnership } from './use-web-push-ownership';
import { WorkspaceNotificationContext } from './workspace-notification-context';
import { useMessagingLinkNotices } from '@/components/connectors/messaging/use-messaging-link-notices';

export function WorkspaceNotificationProvider({
  organizationId,
  orgSlug,
  children,
}: {
  organizationId?: Id<'organizations'>;
  orgSlug?: string;
  children: ReactNode;
}) {
  useMessagingLinkNotices(organizationId);
  const browserPermission = useBrowserNotificationPermission(
    Boolean(organizationId)
  );
  useDesktopNativeNotifications(organizationId, browserPermission);
  // This browser's push subscription belongs to whoever is signed in now.
  useWebPushOwnership(Boolean(organizationId));
  // The inbox lists render on the Activity page; their queries run only
  // while it is open.
  const pathname = usePathname();
  const inboxShown = Boolean(orgSlug) && pathname === `/${orgSlug}/activity`;
  const now = useMinuteClock();
  // A bell failure must not replace the workspace with the route error screen.
  // The minute clock gives a failed subscription a fresh request on its next tick.
  const indicator = useQueryWithStatus(
    api.organizations.threads.sidebar_status.getInboxIndicator,
    orgSlug ? { orgSlug, now } : 'skip'
  );
  const hasPendingQuestion = indicator.data?.hasPendingQuestion ?? false;

  const value = useMemo(
    () => ({
      orgSlug,
      organizationId,
      hasPendingQuestion,
      browserPermission,
    }),
    [browserPermission, hasPendingQuestion, orgSlug, organizationId]
  );

  return (
    <WorkspaceNotificationContext.Provider value={value}>
      <SidebarInboxDataProvider
        orgSlug={orgSlug}
        now={now}
        enabled={inboxShown}
      >
        {children}
      </SidebarInboxDataProvider>
    </WorkspaceNotificationContext.Provider>
  );
}

export function WorkspaceNotificationProviderBridge({
  children,
}: {
  children: ReactNode;
}) {
  const params = useParams();
  const routeSlug = params?.orgSlug;
  const orgSlug = Array.isArray(routeSlug) ? routeSlug[0] : routeSlug;
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    typeof orgSlug === 'string' ? { slug: orgSlug } : 'skip'
  );

  return (
    <WorkspaceNotificationProvider
      organizationId={organization.data?._id}
      orgSlug={typeof orgSlug === 'string' ? orgSlug : undefined}
    >
      {children}
    </WorkspaceNotificationProvider>
  );
}
